
import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, Dropdown, Menu } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StockTransferService } from '../../../Services/Manage/StockTransferService';
import { UserService } from '../../../Services/Manage/UserService';
import {
    EditOutlined, EyeOutlined, SwapOutlined, ClockCircleOutlined,
    CheckCircleOutlined, SendOutlined, CloseCircleOutlined,
    DownloadOutlined, PlusOutlined, FilterOutlined, CalendarOutlined, DownOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const PERMISSION_LINK = '/inventory/stock-transfer';

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const STATUS_COLORS = {
    "Pending": "#faad14",
    "In Transit": "#1677ff",
    "Completed": "#52c41a",
    "Cancelled": "#f5222d",
};

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const StockTransfersDashboard = () => {
    const perm = getPermission(PERMISSION_LINK);
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allSTs, setAllSTs] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);
const [loading, setLoading] = useState(false);
    const [userMap, setUserMap] = useState({});
    const [dateFilterRange, setDateFilterRange] = useState(null); // null = All Time (default)
    const [dateFilterLabel, setDateFilterLabel] = useState('All Time');

    const getAllSTs = async () => {
        setLoading(true);
        try {
            const request = await StockTransferService.getAllST();
            setAllSTs(request.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getUserMap = async () => {
        try {
            const request = await UserService.getAllUsers();
            const map = {};
            (request.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getAllSTs();
        getUserMap();
    }, []);

    const visibleSTs = useMemo(
        () => perm.view_all ? allSTs : allSTs.filter((s) => String(s.added_by) === String(currentUserId)),
        [allSTs, perm.view_all, currentUserId]
    );

    // Date-filtered base for stat cards (Completed/In Transit/Pending/Cancelled)
    // — reacts to the "More Actions" date preset only, not search/status/etc.
    const dateFilteredSTs = useMemo(() => {
        if (!dateFilterRange) return visibleSTs;
        return visibleSTs.filter((s) => {
            const d = dayjs(s.created_at);
            return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
        });
    }, [visibleSTs, dateFilterRange]);

const filteredSTs = useMemo(() => {
        let filtered = visibleSTs;
        if (searchText) {
            filtered = filtered.filter((s) =>
                Object.values(s).some((value) => value?.toString().toLowerCase().includes(searchText.toLowerCase()))
            );
        }
        if (selectedSource) filtered = filtered.filter((s) => s.source_warehouse === selectedSource);
        if (selectedDestination) filtered = filtered.filter((s) => s.destination_warehouse === selectedDestination);
        if (selectedStatus) filtered = filtered.filter((s) => s.status === selectedStatus);
        if (dateFilterRange) {
            filtered = filtered.filter((s) => {
                const d = dayjs(s.created_at);
                return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
            });
        }
        return filtered;
    }, [visibleSTs, searchText, selectedStatus, selectedSource, selectedDestination, dateFilterRange]);

const totalSTs = visibleSTs.length; // always all-time
    const completedCount = useMemo(() => dateFilteredSTs.filter((s) => s.status === "Completed").length, [dateFilteredSTs]);
    const inTransitCount = useMemo(() => dateFilteredSTs.filter((s) => s.status === "In Transit").length, [dateFilteredSTs]);
    const pendingCount = useMemo(() => dateFilteredSTs.filter((s) => s.status === "Pending").length, [dateFilteredSTs]);
    const cancelledCount = useMemo(() => dateFilteredSTs.filter((s) => s.status === "Cancelled").length, [dateFilteredSTs]);

    const statusSummaryData = useMemo(() => {
        const counts = { "Completed": completedCount, "In Transit": inTransitCount, "Pending": pendingCount, "Cancelled": cancelledCount };
        return Object.entries(counts).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] }));
    }, [completedCount, inTransitCount, pendingCount, cancelledCount]);
    const statusSummaryTotal = statusSummaryData.reduce((a, b) => a + b.value, 0);

    const thisMonthStats = useMemo(() => {
        const now = new Date();
        const curMonth = now.getMonth();
        const curYear = now.getFullYear();
        const monthSTs = visibleSTs.filter((s) => {
            if (!s.transfer_date) return false;
            const d = new Date(s.transfer_date);
            return d.getMonth() === curMonth && d.getFullYear() === curYear;
        });
        return {
            total: monthSTs.length,
            completed: monthSTs.filter((s) => s.status === "Completed").length,
            inTransit: monthSTs.filter((s) => s.status === "In Transit").length,
            pending: monthSTs.filter((s) => s.status === "Pending").length,
            cancelled: monthSTs.filter((s) => s.status === "Cancelled").length,
        };
    }, [visibleSTs]);

const topSourceWarehousesData = useMemo(() => {
        const map = {};
        dateFilteredSTs.forEach((s) => {
            const key = s.source_warehouse || "Unknown";
            map[key] = (map[key] || 0) + Number(s.total_value || 0);
        });
        return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 3);
    }, [dateFilteredSTs]);

    const recentSTs = useMemo(
        () => dateFilteredSTs.slice().sort((a, b) => new Date(b.transfer_date) - new Date(a.transfer_date)).slice(0, 5),
        [dateFilteredSTs]
    );

    const sourceOptions = [...new Set(visibleSTs.map((s) => s.source_warehouse).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const destinationOptions = [...new Set(visibleSTs.map((s) => s.destination_warehouse).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const statusOptions = ["Pending", "In Transit", "Completed", "Cancelled"].map((s) => ({ label: s, value: s }));

    const REPORT_COLUMNS = [
        { label: "Transfer No." },
        { label: "Date" },
        { label: "Source Warehouse" },
        { label: "Destination Warehouse" },
        { label: "Total Items" },
        { label: "Status" },
        { label: "Created By" },
    ];

    const buildReportRows = () => filteredSTs.map((r) => ({
        "Transfer No.": r.transfer_no || "-",
        "Date": r.transfer_date ? new Date(r.transfer_date).toLocaleDateString('en-GB') : '-',
        "Source Warehouse": r.source_warehouse || "-",
        "Destination Warehouse": r.destination_warehouse || "-",
        "Total Items": r.total_items ?? 0,
        "Status": r.status || "-",
        "Created By": userMap[String(r.added_by)] || r.added_by || '-',
    }));

    const dateStamp = () => dayjs().format("YYYY-MM-DD");

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stock Transfer Report");
        XLSX.writeFile(wb, `Stock-Transfer-Report-${dateStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Stock Transfer Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Stock-Transfer-Report-${dateStamp()}.pdf`);
    };

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        { title: 'Transfer No.', dataIndex: 'transfer_no', key: 'transfer_no' },
        { title: 'Date', dataIndex: 'transfer_date', key: 'transfer_date', render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-' },
        { title: 'Source Warehouse', dataIndex: 'source_warehouse', key: 'source_warehouse', render: (v) => v || '-' },
        { title: 'Destination Warehouse', dataIndex: 'destination_warehouse', key: 'destination_warehouse', render: (v) => v || '-' },
        {
            title: 'Total Items',
            key: 'total_items',
            align: 'center',
            render: (_, record) => record.total_items ?? 0,
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <span style={{
                    background: `${STATUS_COLORS[record.status] || '#8c8c8c'}1a`,
                    color: STATUS_COLORS[record.status] || '#8c8c8c',
                    padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>
                    {record.status || '-'}
                </span>
            ),
        },
        {
            title: 'Created By',
            dataIndex: 'added_by',
            key: 'added_by',
            render: (v) => userMap[String(v)] || v || '',
        },
        {
            title: 'Action',
            key: 'Action',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/stock-transfer/view-stock-transfer', { state: { id: record.id, action: 'View', enableEdit: false, from: '/inventory/stock-transfer' } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/stock-transfer/edit-stock-transfer', { state: { id: record.id, action: 'Edit', enableEdit: true, from: '/inventory/stock-transfer' } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Stock Transfer</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Stock Transfer</Typography.Text>
                </div>
<Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    </Dropdown>
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu
                                onClick={({ key }) => {
                                    let range = null;
                                    let label = 'All Time';
                                    if (key === 'today') {
                                        range = [dayjs().startOf('day'), dayjs().endOf('day')];
                                        label = 'Today';
                                    } else if (key === 'last7') {
                                        range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                                        label = 'Last 7 Days';
                                    } else if (key === 'last30') {
                                        range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                                        label = 'Last 30 Days';
                                    } else if (key === 'lastMonth') {
                                        range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                                        label = 'Last Month';
                                    } else if (key === 'thisYear') {
                                        range = [dayjs().startOf('year'), dayjs()];
                                        label = 'This Year';
                                    } else if (key === 'all') {
                                        range = null;
                                        label = 'All Time';
                                    }
                                    setDateFilterRange(range);
                                    setDateFilterLabel(label);
                                }}
                                items={[
                                    { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                                    { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                                    { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                                    { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                                    { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                                    { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                                ]}
                            />
                        }
                    >
                        <Button size="middle" icon={<DownOutlined />}>
                            More Actions ({dateFilterLabel})
                        </Button>
                    </Dropdown>
                    <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                        <Button
                            size="middle" type="primary" disabled={!perm.add}
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/inventory/stock-transfer/add-stock-transfer', { state: { action: 'Add', enableEdit: true, from: '/inventory/stock-transfer' } })}
                            style={{ background: '#1677ff', borderColor: '#1677ff' }}
                        >
                            New Stock Transfer
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total Transfers" value={totalSTs} sub="View all transfers" color="#722ed1" bg="#f5eeff" icon={<SwapOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Completed" value={completedCount} sub="View completed" color="#52c41a" bg="#f6ffed" icon={<CheckCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="In Transit" value={inTransitCount} sub="View in transit" color="#1677ff" bg="#f0f7ff" icon={<SendOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Pending" value={pendingCount} sub="View pending" color="#faad14" bg="#fffbe6" icon={<ClockCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <StatCard title="Cancelled" value={cancelledCount} sub="View cancelled" color="#f5222d" bg="#fff1f0" icon={<CloseCircleOutlined />} />
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} md={6}>
                                    <Input
                                        allowClear
                                        placeholder="Search transfer no, source, destination..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={5}>
                                    <Select allowClear placeholder="All Source Warehouses" options={sourceOptions}
                                        value={selectedSource} onChange={setSelectedSource} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={5}>
                                    <Select allowClear placeholder="All Destination Warehouses" options={destinationOptions}
                                        value={selectedDestination} onChange={setSelectedDestination} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredSTs.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))}
                            columns={columns}
                        />
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Transfer Status Overview</Typography.Text>
                            {statusSummaryTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🔄</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${statusSummaryTotal ? Math.round((v / statusSummaryTotal) * 100) : 0}%)`, n]} />
                                                <Pie data={statusSummaryData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                    {statusSummaryData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{statusSummaryTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total Transfers</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {statusSummaryData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({statusSummaryTotal ? Math.round((e.value / statusSummaryTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>This Month Summary</Typography.Text>
                            <div style={{ marginTop: 10 }}>
                                {[
                                    ["Total Transfers", thisMonthStats.total],
                                    ["Completed", thisMonthStats.completed],
                                    ["In Transit", thisMonthStats.inTransit],
                                    ["Pending", thisMonthStats.pending],
                                    ["Cancelled", thisMonthStats.cancelled],
                                ].map(([label, value], i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <Typography.Text type="secondary">{label}</Typography.Text>
                                        <Typography.Text strong>{value}</Typography.Text>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Top Source Warehouses (By Value)</Typography.Text>
                            {topSourceWarehousesData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🏭</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ marginTop: 12 }}>
                                    {topSourceWarehousesData.map((s, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 10 }}>
                                            <span style={{ color: '#333' }}>{i + 1}. {s.name}</span>
                                            <span style={{ fontWeight: 700, color: '#1658b4' }}>₹ {s.value.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Recent Transfers</Typography.Text>
                            <div style={{ marginTop: 12 }}>
                                {recentSTs.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <span style={{ color: '#1677ff', cursor: 'pointer' }}
                                            onClick={() => navigate('/inventory/stock-transfer/view-stock-transfer', { state: { id: s.id, action: 'View', enableEdit: false, from: '/inventory/stock-transfer' } })}
                                        >{s.transfer_no}</span>
                                        <span style={{ color: '#888' }}>{s.transfer_date ? new Date(s.transfer_date).toLocaleDateString('en-GB') : '-'}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default StockTransfersDashboard;